/**
 * Built-in room-shape templates.
 *
 * This module is deliberately DOM-, account-, network-, and IndexedDB-free so
 * the same catalog can drive the app's offline template library and standalone
 * training material. Coordinates are inches, not display-only pixels: every
 * document carries a 1 px/in scale and measured lengths so using a template
 * never re-autoscales it. Field Capture can still replace any nominal value.
 */
import { toCvAngle } from '../geometry/angles.js';
import { duplicateSketchDoc, serializeSketch } from '../store/sketch-store.js';

const PREFIX = 'base-room:';
const BUILT_IN_AT = '2026-08-16T00:00:00.000Z';

function shape(id, name, points, { closingSegmentKind = null } = {}) {
  return Object.freeze({ id: `${PREFIX}${id}`, name, points: points.map(([x, y]) => Object.freeze({ x, y })), closingSegmentKind });
}

/** The globally available base shapes, in the owner's requested training order. */
export const BASE_ROOM_TEMPLATES = Object.freeze([
  shape('reverse-p', 'Reverse P shape', [[80, 100], [80, 0], [0, 0], [0, 45], [40, 45], [40, 100]], { closingSegmentKind: 'wall' }),
  shape('p', 'P shape', [[0, 100], [0, 0], [80, 0], [80, 45], [40, 45], [40, 100]], { closingSegmentKind: 'wall' }),
  shape('rectangle-left-only', 'Rectangle left only', [[30, 100], [0, 100], [0, 0], [120, 0], [120, 100]], { closingSegmentKind: 'wall' }),
  shape('wide-ric-double-bifold', 'Wide RIC with double bifold', [[30, 72], [0, 72], [0, 0], [180, 0], [180, 72], [150, 72]], { closingSegmentKind: 'wall' }),
  shape('pantry-45-left', 'Pantry 45 left', [[30, 100], [0, 70], [0, 0], [120, 0], [120, 100]], { closingSegmentKind: 'wall' }),
  shape('three-walls-open', '3 walls open', [[0, 100], [0, 0], [120, 0], [120, 100]]),
  shape('rectangle-right-only', 'Rectangle right only', [[0, 100], [0, 0], [120, 0], [120, 100], [90, 100]], { closingSegmentKind: 'wall' }),
  shape('pantry-45-right', 'Pantry 45 Right', [[0, 100], [0, 0], [120, 0], [120, 70], [90, 100]], { closingSegmentKind: 'wall' }),
  shape('rectangle-both', 'Rectangle both', [[30, 100], [0, 100], [0, 0], [120, 0], [120, 100], [90, 100]], { closingSegmentKind: 'wall' }),
]);

const byId = new Map(BASE_ROOM_TEMPLATES.map((template) => [template.id, template]));

export function isBaseRoomTemplateId(id) {
  return byId.has(id);
}

function wallFromPoints(templateId, start, end, sequenceIndex, segmentKind, wallNumber) {
  const rawAngle = (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;
  const internalAngle = ((rawAngle % 360) + 360) % 360;
  return {
    id: `${templateId}:segment-${sequenceIndex + 1}`,
    segmentKind,
    internalAngle,
    angleDeg: toCvAngle(internalAngle),
    start: { ...start },
    end: { ...end },
    isMeasured: true,
    lengthUnits: Math.hypot(end.x - start.x, end.y - start.y),
    sequenceIndex,
    wallNumber,
    includeInElevation: segmentKind === 'wall',
  };
}

/** Returns a fresh JSON-safe template document for thumbnails or duplication. */
export function baseRoomTemplateDocument(id) {
  const template = byId.get(id);
  if (!template) return null;
  let wallNumber = 0;
  const walls = [];
  for (let index = 0; index < template.points.length - 1; index++) {
    wallNumber += 1;
    walls.push(wallFromPoints(template.id, template.points[index], template.points[index + 1], index, 'wall', wallNumber));
  }
  if (template.closingSegmentKind) {
    const closingWallNumber = template.closingSegmentKind === 'wall' ? wallNumber + 1 : null;
    walls.push(wallFromPoints(
      template.id,
      template.points[template.points.length - 1],
      template.points[0],
      walls.length,
      template.closingSegmentKind,
      closingWallNumber,
    ));
  }
  return serializeSketch({
    walls,
    scalePxPerUnit: 1,
    selectedWallId: walls.find((wall) => wall.segmentKind === 'wall')?.id ?? null,
    snapClosed: true,
    closedPerimeter: Boolean(template.closingSegmentKind),
  }, {
    id: template.id,
    client: '', opportunity: '', space: '', version: 'Template',
    isPreferred: false, isTemplate: true, templateName: template.name,
    templateRank: BASE_ROOM_TEMPLATES.indexOf(template),
  }, BUILT_IN_AT);
}

/** Creates an independent normal sketch from a built-in shape. */
export function instantiateBaseRoomTemplate(id, { sketchId, makeId, nowIso } = {}) {
  const doc = baseRoomTemplateDocument(id);
  if (!doc || typeof sketchId !== 'string' || typeof makeId !== 'function') return null;
  return duplicateSketchDoc(doc, { id: sketchId, makeId, nowIso, fromTemplate: true });
}
